/**
 * API Error handling utilities.
 *
 * Provides functions to parse and format API error responses into user-friendly messages.
 */
import { getI18nInstance } from './translationHelpers';

export interface ApiError {
  detail:
    | string
    | { loc: (string | number)[]; msg: string; type: string }[]
    | Record<string, unknown>;
  /**
   * Machine-readable error code. A sibling of `detail`, never nested inside it,
   * so getErrorMessage() keeps working for every existing caller.
   */
  code?: string;
  /** Seconds to wait, present on 429 responses that carry a cooldown. */
  retry_after_seconds?: number;
  /** Email address associated with the error (e.g. email_not_verified). */
  email?: string;
}

export interface ValidationError {
  field: string;
  message: string;
}

/**
 * Extract a user-friendly error message from API error response.
 *
 * Handles both simple string errors and complex Pydantic validation errors.
 *
 * @param error - The API error response object
 * @returns A user-friendly error message
 */
export function getErrorMessage(error: ApiError): string {
  if (typeof error.detail === 'string') {
    return error.detail;
  }

  if (Array.isArray(error.detail)) {
    // Handle Pydantic validation errors
    const firstError = error.detail[0];
    const field = firstError.loc[firstError.loc.length - 1];
    return `${field}: ${firstError.msg}`;
  }

  if (error.detail != null && typeof error.detail === 'object') {
    // Plain-object detail (a 422 with a machine-readable body — e.g. the
    // target-price compute gate's {"stage", "reason"}). A mapped reason
    // localizes through the i18n instance (PR-review fix F — fetched via the
    // translationHelpers registry, NOT a direct i18n/config import: that
    // would pull react-i18next into this util's module graph and explode
    // under react-i18next-mocking tests); anything else degrades to the
    // GENERIC unknown-error string — never a JSON.stringify rendering, which
    // would leak internal detail (stage names, machine reasons) app-wide
    // (PR-review fix E). Instance not yet initialized → the English default.
    const detail = error.detail as Record<string, unknown>;
    if (detail.reason === 'confirm-all-stages-first') {
      const i18n = getI18nInstance();
      return i18n
        ? i18n.t('chat.targetPrice.confirmAllStagesFirst', {
            defaultValue: 'Confirm all stages first',
          })
        : 'Confirm all stages first';
    }
    return 'An unexpected error occurred';
  }

  return 'An unexpected error occurred';
}

/**
 * Parse API error into structured validation error format.
 *
 * @param error - The API error response object
 * @returns Array of validation errors with field and message
 */
export function parseValidationErrors(error: ApiError): ValidationError[] {
  if (typeof error.detail === 'string') {
    return [{ field: 'general', message: error.detail }];
  }

  if (Array.isArray(error.detail)) {
    return error.detail.map((err) => {
      const field = err.loc[err.loc.length - 1];
      return {
        field: String(field),
        message: err.msg,
      };
    });
  }

  return [{ field: 'general', message: 'An unexpected error occurred' }];
}

/**
 * Check if an error is a validation error (422 status).
 *
 * @param error - The error to check
 * @returns True if the error is a validation error
 */
export function isValidationError(error: any): error is ApiError {
  return (
    error &&
    typeof error === 'object' &&
    'detail' in error &&
    (typeof error.detail === 'string' || Array.isArray(error.detail))
  );
}

/**
 * An API failure that keeps its status and machine-readable code.
 *
 * Extends Error rather than replacing it, so every existing
 * `error instanceof Error ? error.message : fallback` catch block in the app
 * keeps working unchanged.
 */
export class RequestError extends Error {
  readonly status: number;
  readonly code?: string;
  readonly retryAfterSeconds?: number;
  readonly email?: string;

  constructor(
    message: string,
    status: number,
    code?: string,
    retryAfterSeconds?: number,
    email?: string
  ) {
    super(message);
    this.name = 'RequestError';
    this.status = status;
    this.code = code;
    this.retryAfterSeconds = retryAfterSeconds;
    this.email = email;
  }
}

/**
 * Backend error code -> i18n key.
 *
 * The server's `detail` is English-only copy. Screens translate by code and fall
 * back to `detail` for a code this map does not know, so a new backend code
 * degrades to readable English instead of to a raw key string.
 */
export const ERROR_MESSAGE_KEYS: Record<string, string> = {
  email_not_verified: 'auth.errEmailNotVerified',
  invalid_code: 'auth.errInvalidCode',
  code_expired: 'auth.errCodeExpired',
  code_attempts_exceeded: 'auth.errCodeAttemptsExceeded',
  resend_too_soon: 'auth.errResendTooSoon',
  rate_limited: 'auth.errRateLimited',
  email_quota_exhausted: 'auth.errQuotaExhausted',
  invalid_password: 'auth.errInvalidPassword',
  email_already_in_use: 'auth.errEmailAlreadyInUse',
  username_already_taken: 'auth.errUsernameAlreadyTaken',
  email_matches_username: 'auth.errEmailMatchesUsername',
};

/**
 * Turn a failed Response into a RequestError.
 *
 * Reads the body defensively: a proxy 502 or a Cloud Run HTML error page has no
 * JSON in it, and a parse failure there must not hide the real status code.
 */
export async function toRequestError(response: Response): Promise<RequestError> {
  let body: ApiError | undefined;
  try {
    body = (await response.json()) as ApiError;
  } catch {
    body = undefined;
  }

  const message = body ? getErrorMessage(body) : `Request failed with status ${response.status}`;

  return new RequestError(
    message,
    response.status,
    body?.code,
    body?.retry_after_seconds,
    body?.email
  );
}
